import React, { useRef, useState } from 'react';
import LoadingSpinner from '../LoadingSpinner';
import LinkIdInput from '../common/LinkIdInput';
import EmailChangeModal from './EmailChangeModal';
import fetchWithToken from '../../services';

const ProfileSection = ({
  displayName,
  setDisplayName,
  email,
  setEmail,
  linkId,
  setLinkId,
  icon,
  setIcon,
  setOriginalIcon,
  setUserIcon,
  iconFile,
  setIconFile,
  originalDisplayName,
  setOriginalDisplayName,
  originalEmail,
  setOriginalEmail,
  originalLinkId,
  setOriginalLinkId,
  isLoading,
  setIsLoading,
  profileMessage,
  setProfileMessage,
  signupMethod
}) => {
  const fileInputRef = useRef(null);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [emailStep, setEmailStep] = useState('verify-current'); // 'verify-current', 'enter-new-email', 'verify-new-email', 'success'
  const [verificationCode, setVerificationCode] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [emailChangeInitiated, setEmailChangeInitiated] = useState(false);
  const [isLinkIdValid, setIsLinkIdValid] = useState(true);
  const [isModeratingIcon, setIsModeratingIcon] = useState(false);

  const hasProfileChanges = () => {
    return (
      displayName !== originalDisplayName ||
      email !== originalEmail ||
      iconFile !== null ||
      linkId !== originalLinkId
    );
  };

  const getChangedFields = () => {
    const changedFields = {};

    if (displayName !== originalDisplayName) {
      changedFields.displayName = displayName;
    }

    if (linkId !== originalLinkId) {
      changedFields.linkId = linkId;
    }

    return changedFields;
  };

  // Handle icon click to trigger file input
  const handleIconClick = (ref) => {
    ref.current.click();
  };

  // Handle icon file change with validation + Azure Content Safety pre-check
  const handleIconChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    // Reset any prior message
    setProfileMessage({ text: '', type: '' });

    // Validate type
    const allowedTypes = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/gif']);
    if (!allowedTypes.has(file.type)) {
      setProfileMessage({ text: 'Unsupported image type. Allowed: JPEG, PNG, WebP, GIF.', type: 'error' });
      e.target.value = '';
      return;
    }

    // Validate size (<= 10MB)
    const maxBytes = 10 * 1024 * 1024;
    if (file.size > maxBytes) {
      setProfileMessage({ text: 'Image is too large. Maximum size is 10MB.', type: 'error' });
      e.target.value = '';
      return;
    }

    // Validate dimensions (>= 128x128)
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = async () => {
      const { width, height } = img;
      URL.revokeObjectURL(objectUrl);
      if (!width || !height || Math.min(width, height) < 128) {
        setProfileMessage({ text: 'Image is too small. Minimum dimensions are 128×128.', type: 'error' });
        e.target.value = '';
        return;
      }

      // Passed dimension validation: run moderation pre-check before previewing
      try {
        setIsModeratingIcon(true);

        // Read file as Data URL (base64) for moderation and preview
        const dataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        // Call server pre-check endpoint (authenticated)
        const response = await fetchWithToken('/moderate-profile-icon', {
          method: 'POST',
          body: JSON.stringify({ imageBase64: dataUrl })
        });

        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          setProfileMessage({ text: err.message || 'Image failed content safety review.', type: 'error' });
          e.target.value = '';
          return;
        }

        // Moderation passed: set file and preview
        setIconFile(file);
        setIcon(dataUrl);
      } catch (err) {
        console.error('Error moderating image:', err);
        setProfileMessage({ text: 'Could not review image. Please try a different file.', type: 'error' });
        e.target.value = '';
      } finally {
        setIsModeratingIcon(false);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      setProfileMessage({ text: 'Could not read image. Please try a different file.', type: 'error' });
      e.target.value = '';
    };
    img.src = objectUrl;
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();

    if (!hasProfileChanges()) {
      setProfileMessage({ text: 'No changes to update.', type: 'error' });
      return;
    }

    setIsLoading(true);
    setProfileMessage({ text: '', type: '' });

    try {
      // First update the text fields
      const changedFields = getChangedFields();
      let updateSuccess = true;

      console.log('value of originalLinkId before update: ', originalLinkId)

      if (Object.keys(changedFields).length > 0) {
        const response = await fetchWithToken('/update-user-profile', {
          method: 'POST',
          body: JSON.stringify(changedFields),
        });

        const data = await response.json();

        console.log('updated user object', data)



        if (response.ok) {
          setOriginalDisplayName(displayName);
          setOriginalLinkId(linkId);
        } else if (response.status === 422) {
          // Moderation failures from server
          const serverMsg = (data && data.message) ? String(data.message) : '';
          let text = serverMsg || 'Update failed due to content policy.';
          const msgLc = serverMsg.toLowerCase();
          if (changedFields.displayName && (msgLc.includes('display name') || msgLc.includes('violates'))) {
            text = 'Display name violates our content policy. Please choose another name.';
          } else if (changedFields.linkId && (msgLc.includes('custom link') || msgLc.includes('violates'))) {
            text = 'Custom link violates our content policy. Please choose another one.';
          }
          setProfileMessage({ text, type: 'error' });
          setIsLoading(false);
          return; // Stop further processing (e.g., icon upload)
        } else {
          setProfileMessage({
            text: data.message || 'Failed to update profile',
            type: 'error'
          });
          setIsLoading(false);
          return; // Stop further processing on generic failure
        }
      }

      console.log('value of originalLinkId after update: ', originalLinkId)

      // Then handle icon upload if there is a new icon
      if (iconFile && updateSuccess) {
        const formData = new FormData();
        formData.append('icon', iconFile);

        const iconResponse = await fetchWithToken('/update-profile-icon', {
          method: 'POST',
          body: formData,
        });

        if (iconResponse.ok) {
          const iconData = await iconResponse.json();
          setIcon(iconData.icon);
          setOriginalIcon(iconData.icon);
          setIconFile(null);

          // Update the icon in AppContext to propagate changes to ProfileIcon component
          setUserIcon(iconData.icon);
        } else {
          const errorData = await iconResponse.json();
          setProfileMessage({ text: errorData.message || 'Failed to upload profile icon', type: 'error' });
          updateSuccess = false;
        }
      }

      // Show success message if everything went well
      if (updateSuccess) {
        setProfileMessage({ text: 'Profile updated successfully', type: 'success' });
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setProfileMessage({ text: 'An error occurred while updating your profile', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const initiateEmailChange = () => {
    setEmailStep('verify-current');
    setVerificationCode('');
    setNewEmail('');
    setEmailError('');
    setIsEmailModalOpen(true);
    sendVerificationCode(email);
  };

  const sendVerificationCode = async (emailAddress) => {
    try {
      const response = await fetchWithToken('/send-verification-email', {
        method: 'POST',
        body: JSON.stringify({ type: 'emailChange' }),
      });

      if (!response.ok) {
        throw new Error('Failed to send verification code');
      }
    } catch (error) {
      console.error('Error sending verification code:', error);
      setEmailError('Failed to send verification code. Please try again.');
    }
  };


  return (
    <div className={`bg-white rounded-lg p-6 text-black ${signupMethod === 'google' ? 'h-full flex flex-col' : 'md:row-span-2 h-full'}`}>
      <h2 id="profile-heading" className="text-xl font-semibold mb-4">Profile</h2>

      {/* Profile Icon */}
      <div className="flex justify-center mb-6">
        <div
          className="relative cursor-pointer"
          onClick={() => handleIconClick(fileInputRef)}
        >
          <div
            className="w-24 h-24 rounded-full bg-[#68C7CC] overflow-hidden flex items-center justify-center border-2 border-white hover:opacity-90 transition-opacity"
          >
            {icon ? (
              <img
                src={icon}
                alt="Profile icon"
                className="w-full h-full object-cover object-center"
              />
            ) : (
              <div className="text-white text-4xl font-bold">
                {displayName ? `${displayName[0]}` : '?'}
              </div>
            )}
          </div>
          {isModeratingIcon && (
            <div className="absolute top-0 left-0 w-24 h-24 rounded-full bg-black/40 flex items-center justify-center">
              <LoadingSpinner className="w-6 h-6 text-white" />
            </div>
          )}
          <div className="absolute bottom-0 right-0 bg-[#68C7CC] text-white rounded-full w-8 h-8 flex items-center justify-center border-2 border-white">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zM18.75 10.5h.008v.008h-.008V10.5z" />
            </svg>
          </div>
        </div>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleIconChange}
          accept="image/jpeg,image/png,image/webp,image/gif"
          className="hidden"
        />
      </div>

      {/* Email Change Modal */}
      <EmailChangeModal
        isOpen={isEmailModalOpen}
        currentEmail={email}
        setEmail={setEmail}
        setOriginalEmail={setOriginalEmail}
        step={emailStep}
        email={email}
        emailStep={emailStep}
        setEmailStep={setEmailStep}
        verificationCode={verificationCode}
        setVerificationCode={setVerificationCode}
        sendVerificationCode={sendVerificationCode}
        newEmail={newEmail}
        setNewEmail={setNewEmail}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
        error={emailError}
        setEmailError={setEmailError}
        setIsEmailModalOpen={setIsEmailModalOpen}
      />

      <form onSubmit={handleProfileUpdate} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 md:gap-4 items-center">
          <label htmlFor="displayName" className="font-semibold text-black md:text-right md:pr-4">Display name:</label>
          <div className="md:col-span-2 max-md:pb-2">
            <input
              id="displayName"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="w-full p-2 rounded border-2 border-[#68C7CC]"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 md:gap-4 items-center">
          <label htmlFor="linkId" className="font-semibold text-black md:text-right md:pr-4">Link:</label>
          <div className="md:col-span-2 w-auto max-md:pb-2">
            <LinkIdInput
              originalLinkId={originalLinkId}
              linkId={linkId}
              setLinkId={setLinkId}
              isLinkIdValid={isLinkIdValid}
              setIsLinkIdValid={setIsLinkIdValid}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 md:gap-4 items-center">
          <label htmlFor="email" className="font-semibold text-black md:text-right md:pr-4">Email:</label>
          <div className="md:col-span-2 flex items-center">
            <span className="flex-grow p-2 bg-gray-100 rounded border-2 border-transparent">
              {email}
            </span>
          </div>
        </div>

        {signupMethod !== 'google' && (
          <div className="grid grid-cols-1 md:grid-cols-3 pb-2">
            <div></div>
            <div className="md:col-span-2">
              <button
                type="button"
                onClick={initiateEmailChange}
                className="font-medium text-tipl-coral hover:text-tipl-coral-dark"
              >
                Change email
              </button>
            </div>
          </div>
        )}

        {profileMessage.text && (
          <div className={`col-span-3 text-center ${profileMessage.type === 'success' ? 'text-green-500' : 'text-red-500'}`}>
            {profileMessage.text}
          </div>
        )}

        <div className="col-span-3 flex justify-center">
          <button
            type="submit"
            className={`px-4 py-2 font-semibold rounded transition-colors duration-200 ${hasProfileChanges() && !isLoading && isLinkIdValid
              ? 'bg-green-500 text-white hover:bg-green-600'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            disabled={!hasProfileChanges() || isLoading || !isLinkIdValid}
          >
            {isLoading ? (
              <>
                Updating
                <LoadingSpinner className="inline-block ml-2 w-4 h-4" />
              </>
            ) : (
              'Update'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProfileSection;
